import React from 'react'
import { Text, TouchableOpacity, View, type ViewProps } from 'react-native'

/** The tab continues the panel's right edge without a rounded shoulder. */
export function ModalTabSurface({ style, ...props }: ViewProps) {
  return <View {...props} className="bg-paper border-2 border-olive rounded-2xl overflow-hidden"
    style={[style, { borderTopRightRadius: 0 }]} />
}

interface ModalCloseTabProps {
  onPress: () => void
  accessibilityLabel: string
  testID?: string
}

/** Attached folder-style close affordance shared by compact in-game modals. */
export function ModalCloseTab({
  onPress,
  accessibilityLabel,
  testID,
}: ModalCloseTabProps) {
  const [pressed, setPressed] = React.useState(false)
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={1}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      testID={testID}
      className="absolute bg-paper border-2 border-olive rounded-t-2xl items-center justify-center"
      hitSlop={{ top: 12, left: 8, right: 8 }}
      style={{
        top: -22,
        right: 0,
        width: 80,
        height: 24,
        borderBottomWidth: 0,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        zIndex: 2,
        elevation: 2,
      }}
    >
      <View
        accessible={false}
        className="absolute bg-paper"
        style={{ left: 0, right: 0, bottom: -3, height: 5 }}
      />
      <View pointerEvents="none" className="items-center justify-center rounded-lg"
        style={{ width: 64, height: 20, transform: [{ translateY: pressed ? 1 : 0 }] }}>
        <View className="absolute inset-0 rounded-lg bg-olive" style={{ opacity: pressed ? 0.25 : 0 }} />
        <Text className="text-ink text-lg leading-5">×</Text>
      </View>
    </TouchableOpacity>
  )
}

/**
 * The plain in-game panel (maintainer, 2026-11-03): a rounded paper box with a
 * strip across its top holding a plain × at the right, so nothing in the
 * panel ever sits under the close button. Replaces the folder tab.
 */
export function PlainPanelSurface({ onClose, closeLabel, closeTestID, closeInset = 14, title, style, children, ...props }: ViewProps & {
  onClose: () => void
  closeLabel: string
  closeTestID?: string
  /** The ×'s right edge this far in from the box's, to line up with the body's padding. */
  closeInset?: number
  /** A heading on the ×'s strip, at the left (same inset as the body), so it costs no row of its own. */
  title?: React.ReactNode
}) {
  return (
    <View {...props} className="bg-paper border-2 border-olive rounded-2xl overflow-hidden" style={style}>
      <View style={{ height: 28, flexDirection: 'row', justifyContent: title ? 'space-between' : 'flex-end', alignItems: 'center', paddingLeft: title ? closeInset : 0, paddingRight: Math.max(0, closeInset - 10) }}>
        {title ? <View testID="plain-panel-title" style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>{title}</View> : null}
        <TouchableOpacity onPress={onClose} accessibilityLabel={closeLabel} accessibilityRole="button" testID={closeTestID} hitSlop={10}
          style={{ width: 30, height: 30, alignItems: 'center', justifyContent: 'center' }}>
          <Text className="text-ink font-stencil" style={{ fontSize: 18, lineHeight: 20 }}>×</Text>
        </TouchableOpacity>
      </View>
      {children}
    </View>
  )
}
